import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface GuestCountState {
  tableId: string | null;
  count: number;
}

/**
 * Structural shape the selectors read through. Deliberately not the full
 * RootState: consumers are rendered in tests that stub the store with only the
 * slices they care about, so the slice has to be optional.
 */
export interface WithGuestCount {
  guestCount?: GuestCountState;
}

export interface GuestGateInput {
  // `unknown` rather than number: both callers read this straight off a table
  // detail whose fields are loosely typed ([key: string]: unknown), so the
  // coercion belongs here rather than at every call site.
  capacity?: unknown;
  activeOrder?: unknown;
  answered?: number | null;
}

// Guest count captured by the cashier's pre-menu gate (WRDS-1202).
//
// Deliberately in-memory only: the count is asked before an order exists, and
// the backend has nowhere to put it until the first item opens one. If the
// cashier backs out to the dashboard before adding an item, the answer is
// dropped and they are asked again — a shared terminal must never carry a
// stale count from a previous party into a new one.
//
// Scoped by tableId so a count answered for one table can never be read by
// another; a selector that finds a different tableId reports "not answered".
const initialState: GuestCountState = {
  tableId: null,
  count: 0,
};

const guestCountSlice = createSlice({
  name: 'guestCount',
  initialState,
  reducers: {
    setGuestCount(
      state,
      action: PayloadAction<{ tableId?: string | null; count?: number | string }>,
    ) {
      state.tableId = action.payload?.tableId ?? null;
      state.count = Number(action.payload?.count) || 0;
    },
    clearGuestCount(state) {
      state.tableId = null;
      state.count = 0;
    },
  },
});

/**
 * Count answered for this table, or 0 when the gate has not been passed.
 *
 * Optional-chained on purpose: a missing slice must read as "not answered"
 * rather than throwing inside an unrelated component.
 */
export const selectGuestCountFor =
  (tableId?: string | null) =>
  (state: WithGuestCount): number =>
    state?.guestCount?.tableId &&
    String(state.guestCount.tableId) === String(tableId)
      ? state.guestCount.count
      : 0;

/**
 * Whether the cashier still owes an answer for this table.
 *
 * Lives here rather than in a component because two sibling subtrees depend on
 * it — the category page renders the gate, and the layout blurs the checkout
 * sidebar alongside it. They must never disagree about whether the gate is up.
 *
 * False when:
 *   - the table already has an activeOrder. The party is seated and counted, so
 *     a cashier adding a second round is never re-asked. This also covers the
 *     edit-order, split-pay and edit-sales flows, which all operate on an order
 *     that already exists.
 *   - capacity <= 1, trivially one guest; also covers the takeaway/no-table
 *     flows where capacity is 0 or absent.
 *   - a count was already answered for this table.
 */
export const isGuestGateAsking = ({
  capacity,
  activeOrder,
  answered,
}: GuestGateInput): boolean =>
  !((Number(capacity) || 0) <= 1 || activeOrder || answered);

export const { setGuestCount, clearGuestCount } = guestCountSlice.actions;
export default guestCountSlice.reducer;
